/**
 * MarketingRibbonCard — super-admin editor for the audience-aware trial
 * ribbon shown on the landing page.
 *
 * Loads config from `GET /api/admin/marketing/ribbon-config`, lets the
 * admin edit every field (enabled toggle, default audience, and each
 * variant's body / emphasis / CTA / href for both buyer and seller pools),
 * then persists via `PUT /api/admin/marketing/ribbon-config`.
 *
 * Design: read-modify-write on the whole doc — variant lists are small
 * (typically 3 rows per audience) so we don't need per-row PATCH endpoints.
 * A "Reset to defaults" button wipes the persisted doc so the shipped
 * copy comes back.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Loader2, RotateCcw, Save, Plus, Trash2, Users, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

type Audience = "buyer" | "seller";

interface Variant {
  id: string;
  body: string;
  emphasis: string;
  cta: string;
  href: string;
}

interface RibbonConfig {
  enabled: boolean;
  default_audience: Audience;
  buyer_variants: Variant[];
  seller_variants: Variant[];
}

const EMPTY_VARIANT = (audience: Audience, n: number): Variant => ({
  id: `${audience}-${Date.now().toString(36)}-${n}`,
  body: "",
  emphasis: "",
  cta: "",
  href: audience === "buyer" ? "/shop" : "/signup",
});

// ── Row editor ──────────────────────────────────────────────────────────
const VariantRow: React.FC<{
  variant: Variant;
  onChange: (next: Variant) => void;
  onRemove: () => void;
  disableRemove: boolean;
  testIdPrefix: string;
}> = ({ variant, onChange, onRemove, disableRemove, testIdPrefix }) => {
  const bind = <K extends keyof Variant>(field: K) => ({
    value: variant[field] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange({ ...variant, [field]: e.target.value }),
    className:
      "w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs focus:border-slate-900 focus:outline-none",
  });
  return (
    <div
      data-testid={`${testIdPrefix}-row-${variant.id}`}
      className="rounded-xl border border-slate-200 bg-slate-50/50 p-3 space-y-2"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="text-[10px] uppercase tracking-wider text-slate-500">
          Body
          <input
            {...bind("body")}
            placeholder="Short hook"
            data-testid={`${testIdPrefix}-body`}
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-slate-500">
          Emphasis
          <input
            {...bind("emphasis")}
            placeholder="Bolded value prop"
            data-testid={`${testIdPrefix}-emphasis`}
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-slate-500">
          CTA
          <input
            {...bind("cta")}
            placeholder="Button label"
            data-testid={`${testIdPrefix}-cta`}
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-slate-500">
          Href
          <input
            {...bind("href")}
            placeholder="/shop or https://…"
            data-testid={`${testIdPrefix}-href`}
          />
        </label>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-mono text-slate-400">{variant.id}</span>
        <button
          type="button"
          onClick={onRemove}
          disabled={disableRemove}
          data-testid={`${testIdPrefix}-remove`}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Trash2 className="h-3 w-3" />
          Remove
        </button>
      </div>
    </div>
  );
};

// ── Audience column ─────────────────────────────────────────────────────
const AudiencePanel: React.FC<{
  title: string;
  subtitle: string;
  audience: Audience;
  variants: Variant[];
  onChange: (next: Variant[]) => void;
  icon: React.ReactNode;
}> = ({ title, subtitle, audience, variants, onChange, icon }) => {
  const testIdPrefix = `ribbon-${audience}`;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`ribbon-panel-${audience}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="h-7 w-7 rounded-lg bg-slate-900 text-white grid place-items-center">
          {icon}
        </span>
        <div>
          <div className="font-display font-semibold text-sm">{title}</div>
          <div className="text-[11px] text-slate-500">{subtitle}</div>
        </div>
      </div>
      <div className="space-y-3 mt-3">
        {variants.map((v, i) => (
          <VariantRow
            key={v.id}
            variant={v}
            onChange={(next) =>
              onChange(variants.map((x, j) => (j === i ? next : x)))
            }
            onRemove={() => onChange(variants.filter((_, j) => j !== i))}
            disableRemove={variants.length <= 1}
            testIdPrefix={`${testIdPrefix}-${i}`}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange([...variants, EMPTY_VARIANT(audience, variants.length)])}
        data-testid={`ribbon-${audience}-add`}
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-slate-900"
      >
        <Plus className="h-3 w-3" /> Add variant
      </button>
    </div>
  );
};

// ── Main card ──────────────────────────────────────────────────────────
export const MarketingRibbonCard: React.FC = () => {
  const [config, setConfig] = useState<RibbonConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/marketing/ribbon-config");
      setConfig(data);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Couldn't load ribbon config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/marketing/ribbon-config", config);
      setConfig(data);
      toast.success("Ribbon copy saved. Live for the next page load.");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!confirm("Wipe custom ribbon copy and restore shipped defaults?")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/marketing/ribbon-config/reset");
      setConfig(data);
      toast.success("Ribbon copy reset to defaults.");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const dirty = useMemo(() => true, [config]); // treat every load as dirty-able

  if (loading || !config) {
    return (
      <div className="sg-card p-6 text-sm text-slate-500" data-testid="marketing-ribbon-card-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
        Loading ribbon config…
      </div>
    );
  }

  return (
    <div className="sg-card p-5 sm:p-6 space-y-5" data-testid="marketing-ribbon-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="sg-label">Landing · Trial ribbon</div>
          <div className="font-display font-bold text-lg">Audience-aware ribbon copy</div>
          <div className="text-xs text-slate-500 max-w-lg mt-0.5">
            Shoppers see shopping-flavored nudges. Brands see the trial pitch. Edit both
            pools below — changes take effect on the next page load.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={reset}
            disabled={saving}
            data-testid="marketing-ribbon-reset"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-900 disabled:opacity-60"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="marketing-ribbon-save"
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 text-white px-3 py-1.5 text-xs font-semibold hover:bg-black disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </button>
        </div>
      </div>

      {/* Top-level toggles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="rounded-xl border border-slate-200 p-3 flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
            data-testid="marketing-ribbon-enabled"
            className="h-4 w-4"
          />
          <div>
            <div className="font-semibold text-sm">Ribbon enabled</div>
            <div className="text-[11px] text-slate-500">Turn the strip on or off site-wide.</div>
          </div>
        </label>
        <label className="rounded-xl border border-slate-200 p-3 flex items-center gap-3">
          <div className="flex-1">
            <div className="font-semibold text-sm">Default audience</div>
            <div className="text-[11px] text-slate-500">Which pool a first-time visitor sees before we can infer intent.</div>
          </div>
          <select
            value={config.default_audience}
            onChange={(e) => setConfig({ ...config, default_audience: e.target.value as Audience })}
            data-testid="marketing-ribbon-default-audience"
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
          >
            <option value="buyer">Buyer (shoppers)</option>
            <option value="seller">Seller (brands)</option>
          </select>
        </label>
      </div>

      {/* Audience panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AudiencePanel
          title="Buyer variants"
          subtitle="Shown to shoppers — avoid SaaS wording."
          audience="buyer"
          variants={config.buyer_variants}
          onChange={(next) => setConfig({ ...config, buyer_variants: next })}
          icon={<ShoppingBag className="h-3.5 w-3.5" />}
        />
        <AudiencePanel
          title="Seller variants"
          subtitle="Shown to brands / creators arriving from /pricing or /for-brands."
          audience="seller"
          variants={config.seller_variants}
          onChange={(next) => setConfig({ ...config, seller_variants: next })}
          icon={<Users className="h-3.5 w-3.5" />}
        />
      </div>
    </div>
  );
};

export default MarketingRibbonCard;
